import { useState } from 'react';
import { toast } from 'sonner';
import {
  Boxes,
  HardHat,
  Link as LinkIcon,
  PackageCheck,
  Plus,
  ScanLine,
  Stethoscope,
  Truck,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { STATIONS, type Asset, type Station } from '@hcc/types';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { ConfirmModal } from '../ui/ConfirmModal';
import {
  useAssetsList,
  useAssignAsset,
  useIssueAssetManually,
  useUnassignAsset,
} from '../../hooks/useAssets';
import { useCurrentUser } from '../../hooks/useAuth';
import { AssetPickerModal } from '../kit/AssetPickerModal';
import { ApiError } from '../../lib/api';

const ISSUE_ROLES = ['coordinator', 'admin', 'super_admin'];

/**
 * Person-centric view of pre-assigned and issued kit. Shows every
 * asset currently linked to the person grouped by station, with a
 * picker per station so a coordinator can pre-assign multiple items
 * to one person in a few clicks — the inverse of the asset-centric
 * "Assign to a person" flow on /admin/assets.
 *
 * On the day, these pre-assignments populate the station Equipment
 * panel automatically when the person is being signed for kit.
 */
export function PreAssignedKitPanel({
  personId,
  personName,
}: {
  personId: string;
  personName: string;
}) {
  const list = useAssetsList({ assignee: personId });
  const assign = useAssignAsset();
  const unassign = useUnassignAsset();
  const issueManual = useIssueAssetManually();
  const me = useCurrentUser();
  const canIssue = (me.data?.roles ?? []).some((r) => ISSUE_ROLES.includes(r));
  const [pickerStation, setPickerStation] = useState<Station | null>(null);
  const [issuing, setIssuing] = useState<Asset | null>(null);

  const grouped: Record<Station, Asset[]> = {
    IT: [],
    PPE: [],
    Tools: [],
    Fleet: [],
    DBSHR: [],
  };
  for (const a of list.data ?? []) {
    grouped[a.station].push(a);
  }

  const total = list.data?.length ?? 0;

  async function onPick(asset: Asset) {
    try {
      await assign.mutateAsync({ id: asset.id, person_id: personId });
      toast.success(`${asset.asset_tag} → ${personName}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'assignment failed';
      toast.error(msg);
    } finally {
      setPickerStation(null);
    }
  }

  async function onUnassign(asset: Asset) {
    if (asset.status === 'issued') {
      toast.error(
        'This asset has already been issued. Use Leaver returns to take it back.',
      );
      return;
    }
    try {
      await unassign.mutateAsync(asset.id);
      toast.success(`${asset.asset_tag} unassigned`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'unassign failed';
      toast.error(msg);
    }
  }

  async function confirmIssue() {
    if (!issuing) return;
    try {
      await issueManual.mutateAsync({ id: issuing.id, person_id: personId });
      toast.success(`${issuing.asset_tag} marked issued to ${personName}`);
      setIssuing(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'could not mark issued';
      toast.error(msg);
    }
  }

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <Boxes className="h-5 w-5 text-success" />
        <h2 className="text-base font-semibold tracking-tight text-fg">
          Pre-assigned kit
        </h2>
        {!list.isLoading ? (
          <span className="text-xs text-fg-dim">
            {total} item{total === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
        {list.isLoading ? (
          <div className="px-4 py-4 text-sm text-fg-muted">Loading…</div>
        ) : total === 0 ? (
          <EmptyState
            icon={<Boxes className="h-6 w-6" />}
            title="No kit pre-assigned yet"
            description="Pick a station below to allocate specific assets (laptop, phone, vehicle, etc) to this person before the day."
          />
        ) : null}

        <div className="divide-y divide-divider/10">
          {STATIONS.map((s) => (
            <StationGroup
              key={s}
              station={s}
              assets={grouped[s]}
              canIssue={canIssue}
              onAdd={() => setPickerStation(s)}
              onUnassign={onUnassign}
              onMarkIssued={(a) => setIssuing(a)}
            />
          ))}
        </div>
      </div>

      {pickerStation ? (
        <AssetPickerModal
          station={pickerStation}
          excludeIds={(list.data ?? []).map((a) => a.id)}
          onClose={() => setPickerStation(null)}
          onPick={onPick}
        />
      ) : null}

      <ConfirmModal
        open={issuing !== null}
        tone="warning"
        icon={PackageCheck}
        title="Mark asset as issued?"
        message={
          issuing ? (
            <>
              Record <span className="font-mono font-medium">{issuing.asset_tag}</span>{' '}
              ({issuing.description}) as issued to{' '}
              <span className="font-medium">{personName}</span> without capturing a
              signature. Use this only for kit handed over outside the app.
            </>
          ) : undefined
        }
        confirmLabel="Mark issued"
        onConfirm={confirmIssue}
        onClose={() => setIssuing(null)}
      />
    </section>
  );
}

const STATION_META: Record<Station, { icon: LucideIcon; label: string }> = {
  IT: { icon: ScanLine, label: 'IT' },
  PPE: { icon: HardHat, label: 'PPE / Uniform' },
  Tools: { icon: Wrench, label: 'Tools' },
  Fleet: { icon: Truck, label: 'Fleet' },
  DBSHR: { icon: Stethoscope, label: 'DBS / HR' },
};

function StationGroup({
  station,
  assets,
  canIssue,
  onAdd,
  onUnassign,
  onMarkIssued,
}: {
  station: Station;
  assets: Asset[];
  canIssue: boolean;
  onAdd: () => void;
  onUnassign: (a: Asset) => void;
  onMarkIssued: (a: Asset) => void;
}) {
  const { icon: Icon, label } = STATION_META[station];
  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-fg-muted">
          <Icon className="h-3.5 w-3.5" />
          {label}
          <span className="text-[10px] font-normal text-fg-dim">
            {assets.length}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-3.5 w-3.5" />}
          onClick={onAdd}
        >
          Add asset
        </Button>
      </div>

      {assets.length === 0 ? (
        <div className="mt-1 text-xs text-fg-dim">
          No {label} kit pre-assigned.
        </div>
      ) : (
        <ul className="mt-2 space-y-1">
          {assets.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 rounded-token-sm bg-surface-2 px-3 py-2 text-sm"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to={`/admin/assets/${a.id}`}
                  className="inline-flex items-center gap-2 font-mono text-xs font-medium text-fg hover:underline"
                >
                  <LinkIcon className="h-3 w-3 text-fg-dim" />
                  {a.asset_tag}
                </Link>
                <div className="mt-0.5 truncate text-xs text-fg-muted">
                  {a.description}
                  {a.category ? ` · ${a.category}` : ''}
                </div>
              </div>
              {canIssue && a.status !== 'issued' && a.status !== 'retired' ? (
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft={<PackageCheck className="h-3.5 w-3.5" />}
                  onClick={() => onMarkIssued(a)}
                  title="Mark as issued (no signature), for kit handed over outside the app"
                >
                  Mark issued
                </Button>
              ) : null}
              <StatusPill status={a.status} />
              <button
                type="button"
                onClick={() => onUnassign(a)}
                disabled={a.status === 'issued'}
                aria-label={`Unassign ${a.asset_tag}`}
                title={
                  a.status === 'issued'
                    ? 'Already issued — handle via Leaver returns'
                    : 'Unassign'
                }
                className="rounded-token-sm p-1 text-fg-muted transition-colors hover:bg-danger-soft/15 hover:text-danger disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: Asset['status'] }) {
  const tone =
    status === 'issued'
      ? 'bg-success-soft/20 text-success-soft-fg ring-success-soft/30'
      : status === 'assigned'
        ? 'bg-info-soft/20 text-info-soft-fg ring-info-soft/30'
        : 'bg-neutral-soft/15 text-fg-muted ring-line/15';
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 ${tone}`}
    >
      {status}
    </span>
  );
}
